import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useSnackbar } from "notistack";
import React, { useState } from "react";

import { handleUserFinancialDonation } from "../../userDashboard/userControllers/donationController";
import { formatCurrency } from "../../utils/currency";
import Payform from "../../utils/payForm";
import transactionReconfirmationService from "../../utils/transactionReconfirmation";

import close from "./assets/cross.svg";
import load from "./assets/load.gif";

const Support: React.FC = () => {
    const type = "Financial";
    const [donation_type, setDonation_Type] = useState("");
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [note, setNote] = useState("");
    const [useramount, setUserAmount] = useState<number | "">("");
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [mode, setMode] = useState("card");
    const [status] = useState("Pending");
    const { enqueueSnackbar } = useSnackbar();
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const [payModal, setPayModal] = useState(false);
    const [clientSecret, setClientSecret] = useState("");
    const [transactionId, setTransactionId] = useState("");
    const [amount, setAmount] = useState(0);

    const stripePromise = loadStripe(
        process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ""
    );

    const validateFields = () => {
        const newErrors: Record<string, string> = {};
        if (!name) newErrors.name = "Name is required";
        if (!donation_type) newErrors.donation_type = "Donation type is required";
        if (!phone) newErrors.phone = "Phone is required";
        if (!email) newErrors.email = "Email is required";
        if (!useramount || (typeof useramount === 'number' && useramount <= 0))
            newErrors.useramount = "Valid amount is required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const onSuccess = (response: unknown) => {
        const res = response as {
            data: {
                message: {
                    transaction: {
                        amount: number;
                        client_secret: string;
                        identifier?: string;
                    };
                };
            };
        };
        setAmount(res.data.message.transaction.amount);
        setLoading(false);
        setPayModal(true);
        setClientSecret(res.data.message.transaction.client_secret);
        setTransactionId(res.data.message.transaction.identifier || "");
    };

    const onError = () => {
        setLoading(false);
        enqueueSnackbar("Failed to add donation", { variant: "error" });
    };

    const handleSubmit = () => {
        if (validateFields()) {
            setLoading(true);
            const userData = {
                name,
                email,
                type,
                note,
                donation_type,
                phone,
                amount: useramount,
                mode,
                status,
            };

            handleUserFinancialDonation(userData, onSuccess, onError);
        } else {
            enqueueSnackbar("Please fix the errors in the form", {
                variant: "error",
            });
        }
    };

    const handlePaymentSuccess = async (result: unknown) => {
        const payload = typeof result === "object" && result !== null
            ? result as Record<string, unknown>
            : {};
        const intentValue = payload.payment_intent;
        const paymentIntentId = typeof intentValue === "string"
            ? intentValue
            : typeof intentValue === "object" && intentValue !== null
                ? String((intentValue as Record<string, unknown>).id || "")
                : "";

        if (!transactionId || !paymentIntentId) {
            enqueueSnackbar(
                "Payment received. Confirmation will update automatically.",
                { variant: "info" }
            );
            return;
        }

        const reconfirmation = await transactionReconfirmationService.handleStripeSuccess(
            transactionId,
            { id: paymentIntentId }
        );

        if (reconfirmation.success) {
            setPayModal(false);
            enqueueSnackbar("Donation payment completed.", { variant: "success" });
        } else if (reconfirmation.pending) {
            enqueueSnackbar(
                "Your bank is processing the payment. Confirmation will update automatically.",
                { variant: "info" }
            );
        } else {
            enqueueSnackbar(
                reconfirmation.error || "Payment could not be confirmed.",
                { variant: "error" }
            );
        }
    };

    return (
        <>
            <div className=" w-full space-y-6">
                <div className=" grid  grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 w-full">
                    <label
                        htmlFor="full-name"
                        className=" w-full flex flex-col font-Manrope text-sm font-semibold"
                    >
                        Full Name
                        <input
                            id="full-name"
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className=" mt-2 border bg-white border-[#DAE0E6] rounded-md py-3 px-4 font-Manrope font-normal text-[15px] placeholder:text-[#919BA7]"
                            placeholder="Enter Full Name"
                        />
                        {errors.name && (
                            <span className="text-red-500 text-xs font-Manrope mt-2">
                                {errors.name}
                            </span>
                        )}
                    </label>
                    <label
                        htmlFor="email"
                        className=" w-full flex flex-col font-Manrope text-sm font-semibold"
                    >
                        Email Address
                        <input
                            id="email"
                            type="text"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className=" mt-2 border bg-white border-[#DAE0E6] rounded-md py-3 px-4 font-Manrope font-normal text-[15px] placeholder:text-[#919BA7]"
                            placeholder="Enter Email Address"
                        />
                        {errors.email && (
                            <span className="text-red-500 text-xs font-Manrope mt-2">
                                {errors.email}
                            </span>
                        )}
                    </label>
                </div>
                <div className=" grid  grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 w-full">
                    <label
                        htmlFor="phone"
                        className=" w-full flex flex-col font-Manrope text-sm font-semibold"
                    >
                        Phone Number
                        <input
                            id="phone"
                            type="text"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            className=" mt-2 border bg-white border-[#DAE0E6] rounded-md py-3 px-4 font-Manrope font-normal text-[15px] placeholder:text-[#919BA7]"
                            placeholder="Enter Phone Number"
                        />
                        {errors.phone && (
                            <span className="text-red-500 text-xs font-Manrope mt-2">
                                {errors.phone}
                            </span>
                        )}
                    </label>
                    <label
                        htmlFor="donation-type"
                        className=" w-full flex flex-col font-Manrope text-sm font-semibold"
                    >
                        Donation Type
                        <span
                            className=" mt-2 border bg-white border-[#DAE0E6] rounded-md py-3 px-4 font-Manrope font-normal text-[15px] placeholder:text-[#919BA7]"
                        >
                            <select
                                id="donation-type"
                                value={donation_type}
                                onChange={(e) => setDonation_Type(e.target.value)}
                                className=" w-full bg-white outline-none"
                            >
                                <option value="">Select Type </option>
                                <option value="recurring">Recurring Payment </option>
                                <option value="one_time">One-time Donation </option>
                            </select>
                        </span>
                        {errors.donation_type && (
                            <span className="text-red-500 text-xs font-Manrope mt-2">
                                {errors.donation_type}
                            </span>
                        )}
                    </label>
                </div>

                <label
                    htmlFor="amount"
                    className=" w-full flex flex-col font-Manrope text-sm font-semibold"
                >
                    Amount
                    <input
                        id="amount"
                        type="number"
                        min="0"
                        value={useramount}
                        onChange={(e) => {
                            const value = parseInt(e.target.value);
                            if (!isNaN(value) && value >= 0) {
                                setUserAmount(value);
                            } else if (e.target.value === "") {
                                setUserAmount("");
                            }
                        }}
                        className=" mt-2 border bg-white border-[#DAE0E6] rounded-md py-3 px-4 font-Manrope font-normal text-[15px] placeholder:text-[#919BA7]"
                        placeholder="Enter Amount"
                    />
                    {errors.useramount && (
                        <span className="text-red-500 text-xs font-Manrope mt-2">
                            {errors.useramount}
                        </span>
                    )}
                </label>

                <label
                    htmlFor="note"
                    className=" w-full flex flex-col font-Manrope text-sm font-semibold"
                >
                    Note
                    <input
                        id="note"
                        type="text"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        className=" mt-2 border bg-white border-[#DAE0E6] rounded-md py-3 px-4 font-Manrope font-normal text-[15px] placeholder:text-[#919BA7]"
                        placeholder="Anything you'd like to tell us?"
                    />
                </label>

                <div className=" w-full">
                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className=" bg-[#78C257] px-6 py-3 rounded-[6px] text-[#F5F8FE] font-Manrope font-semibold text-base flex justify-center items-center"
                    >
                        {loading ? (
                            <img src={load} className=" w-5" alt="Loading" />
                        ) : (
                            "Submit"
                        )}
                    </button>
                </div>
            </div>

            {payModal && (
                <div className="w-full md:w-[120%] h-full bg-[#1212128d] z-[999] fixed top-0 left-0 md:-left-[20%] p-6 flex justify-center items-center">
                    <div className="md:ml-[20%] h-auto bg-[#FFFFFF] p-3 rounded-[15px] w-full md:w-[600px]">
                        <div className="w-full max-h-[450px] p-2 bg-[#fff] overflow-y-auto">
                            <span className="w-full flex items-center justify-between">
                                <p className="text-xl text-[#272D37] font-bold font-Manrope">
                                    Make Payment
                                </p>
                                <img
                                    onClick={() => setPayModal(false)}
                                    src={close}
                                    className="w-4 cursor-pointer"
                                    alt="Close"
                                />
                            </span>
                            <div className="w-full flex items-center justify-between mt-6">
                                <p className="font-Manrope font-bold text-2xl text-black">
                                    Total Cost
                                </p>
                                <p className="font-Manrope font-bold text-2xl text-black">
                                    {formatCurrency(amount)}
                                </p>
                            </div>
                            {clientSecret !== "" && (
                                <Elements options={{ clientSecret }} stripe={stripePromise}>
                                    <Payform
                                        clientSecret={clientSecret}
                                        transactionId={transactionId}
                                        onSuccess={handlePaymentSuccess}
                                    />
                                </Elements>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Support;
